import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { cloneDeep } from 'lodash';

import { CUSTOM_HOOK_EVENTS, dispatchCustomEvent } from '@/hooks/useEventListener';

import { API } from '@/utils/constants/Apis';
import { parseJson } from '@/utils/helpers/globalHelper';
import { getOidcAccessToken } from '@/utils/helpers/userOidcHelper';
import { MAINTENANCE_URL, NOT_EXITS_URL } from '@utils/constants/RouteContants';

import i18n from '../i18n';
import { setAlertNotification } from '../redux/globalReducer';
import { store } from '../redux/store';
import { API_URL, AXIOS_TIMEOUT, ERROR_CODE, LIST_API_EXCEPTIONS } from '../utils/constants/AppConstants';
import { userManager } from './oidc';

const { t } = i18n;

const listCacheUrls = [API.GET_MASTER_COUNTRIES, API.GET_MASTER_COUNTRIES_PUBLIC];

interface RetriableRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

export class SetupAxios {
  public instance: AxiosInstance;
  private _refreshTokenPromise: Promise<string | null> | null = null;

  constructor(contentType = 'application/json') {
    this.instance = axios.create({
      baseURL: API_URL,
      timeout: AXIOS_TIMEOUT,
      headers: {
        'Content-Type': contentType || 'application/json',
        Authorization: 'Bearer ' + getOidcAccessToken()
      }
    });

    this.instance.interceptors.response.use(
      (response) => {
        // Do something with response data
        return response;
      },
      (error: AxiosError) => {
        const data: any = error?.response?.data || null;
        const url = error?.response?.config?.url;

        if (error.response?.status === 401) {
          // Redirect maintenance page in case system is under maintenance
          if (data === 'MAINTENANCE') {
            window.location.href = MAINTENANCE_URL;
            return;
          }
          return this._handleAuthorization(error);
        }
        // Check if the URL matches the specified pattern
        const urlMatchResult = url ? /^\/([^\/]+\/){3}/.exec(url) : null;
        const isMatch = urlMatchResult && LIST_API_EXCEPTIONS.includes(urlMatchResult[0]);
        // Do something with response error
        if (
          !isMatch &&
          (error.code?.includes(ERROR_CODE.FORBIDDEN_ACCESS_DENIED) ||
            error.code?.includes(ERROR_CODE.SECURITY_EXCEPTION) ||
            data?.errorCode?.includes(ERROR_CODE.SECURITY_EXCEPTION))
        ) {
          window.location.href = NOT_EXITS_URL; // Redirect 404 when not permession
          return Promise.reject(error);
        }
        if (!window.navigator.onLine && error.code?.includes(ERROR_CODE.ERR_NETWORK)) {
          // Show message when error network
          try {
            store.dispatch(
              setAlertNotification({
                show: true,
                type: 'error',
                position: 'top',
                message: t(`common:error_network`)
              })
            );
          } catch (err) {}
          return Promise.reject(error);
        }
        if (error.code?.includes(ERROR_CODE.COMMON_EXCEPTION)) {
          // Show message when bad request
          try {
            store.dispatch(
              setAlertNotification({
                show: true,
                type: 'error',
                position: 'top',
                message: t(`common:MSG_089`)
              })
            );
          } catch (err) {}
          return Promise.reject(error);
        }
        if (data?.errorCode?.includes(ERROR_CODE.COMMON_EXCEPTION) || error?.response?.status === 503) {
          // Show message when bad request
          try {
            store.dispatch(
              setAlertNotification({
                show: true,
                type: 'error',
                position: 'top',
                message: t(`common:MSG_089`)
              })
            );
            let handleError = cloneDeep(error);
            delete handleError.response;
            return Promise.reject(handleError);
          } catch (err) {
            return Promise.reject(error);
          }
        }
        if (
          error.response?.status === 400 &&
          data?.fields.some((item: { errorCode: string }) => item.errorCode.includes(ERROR_CODE.NOT_FOUND)) &&
          data.fields.findIndex((f: any) => f.name === 'TemplateId') === -1
        ) {
          try {
            store.dispatch(
              setAlertNotification({
                show: true,
                type: 'error',
                position: 'top',
                message: t(`common:MSG_NOT_FOUND`)
              })
            );
            let handleError = cloneDeep(error);
            return Promise.reject(handleError);
          } catch (err) {
            return Promise.reject(error);
          }
        }
        if (data?.errorCode?.includes(ERROR_CODE.VALIDATION)) {
          const fields = data.fields || [];
          let handleError = cloneDeep(error);
          const isAsynchronousData = fields.length ? fields.map((item: any) => item.errorCode).includes(ERROR_CODE.VERSION_MISMATCH) : false;
          if (isAsynchronousData) {
            const message = `${t('common:MSG_C_015')}`;
            store.dispatch(
              setAlertNotification({
                show: true,
                type: 'error',
                position: 'top',
                message
              })
            );
          }
          return Promise.reject(handleError);
        }
        return Promise.reject(error);
      }
    );
  }

  private async _handleFetchCache(url: string, config: AxiosRequestConfig = {}) {
    const configAPI = await this._getConfig(config);
    if (!listCacheUrls.includes(`${url}`)) {
      return this.instance
        .get(url, {
          ...config,
          ...configAPI
        })
        .then((response) => response);
    }
    const cacheKey = `cache_${url}`;
    const cached = parseJson(localStorage.getItem(cacheKey) ?? '');
    const cacheTime = 30 * 60 * 1000; // Cache duration: 30 minutes
    if (cached && cached?.timestamp + cacheTime > Date.now()) {
      return cached.response;
    }

    // If no valid cache, fetch new data
    try {
      return this.instance
        .get(url, {
          ...config,
          ...configAPI
        })
        .then(
          (
            response // Cache the new data with a timestamp
          ) => {
            localStorage.setItem(cacheKey, JSON.stringify({ response, timestamp: Date.now() }));
            return response;
          }
        );
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  }

  private async _getRefreshToken(): Promise<string | null> {
    try {
      const user = await userManager.signinSilent();
      if (!user?.access_token) throw new Error('No access token');
      return user.access_token;
    } catch (err) {
      dispatchCustomEvent(CUSTOM_HOOK_EVENTS.ACCESS_DENIED);
      return null;
    } finally {
      this._refreshTokenPromise = null;
    }
  }

  private async _handleAuthorization(error: AxiosError) {
    const originConfig: RetriableRequestConfig | undefined = error.config;
    if (!originConfig || originConfig._retry) throw error;
    originConfig._retry = true;

    // If the token in localStorage is already newer than the one on the failed request
    // (renewed by another tab or background silent renew), retry immediately with the new token
    const currentToken = getOidcAccessToken();
    const currentHeader = originConfig.headers?.Authorization?.toString() || '';
    const sentToken = currentHeader.replace(/^Bearer\s+/i, '');

    if (currentToken && sentToken && currentToken !== sentToken) {
      if (!originConfig.headers) originConfig.headers = {};
      originConfig.headers.Authorization = `Bearer ${currentToken}`;
      return this.instance(originConfig);
    }

    this._refreshTokenPromise ??= this._getRefreshToken();
    const token = await this._refreshTokenPromise;
    if (!token) throw error;

    if (!originConfig.headers) originConfig.headers = {};
    originConfig.headers.Authorization = `Bearer ${token}`;

    return this.instance(originConfig);
  }

  private async _getConfig(config: AxiosRequestConfig = {}) {
    const token = getOidcAccessToken();
    const configAPI = config?.headers
      ? {
          ...config.headers,
          Authorization: `Bearer ${token}`
        }
      : {
          headers: {
            Authorization: `Bearer ${token}`
          }
        };
    return configAPI;
  }

  public get = async (url = '', config: AxiosRequestConfig = {}): Promise<AxiosResponse> => {
    return this._handleFetchCache(url, config);
  };

  public post = async (url = '', body = {}, config: AxiosRequestConfig = {}): Promise<AxiosResponse> => {
    const configAPI = await this._getConfig(config);

    return this.instance
      .post(url, body, {
        ...config,
        ...configAPI
      })
      .then((response) => response);
  };

  public patch = async (url = '', body = {}, config: AxiosRequestConfig = {}): Promise<AxiosResponse> => {
    const configAPI = await this._getConfig(config);

    return this.instance
      .patch(url, body, {
        ...config,
        ...configAPI
      })
      .then((response) => response);
  };

  public put = async (url = '', body = {}, config: AxiosRequestConfig = {}): Promise<AxiosResponse> => {
    const configAPI = await this._getConfig(config);

    return this.instance
      .put(url, body, {
        ...config,
        ...configAPI
      })
      .then((response) => response);
  };

  public delete = async (url = '', config: AxiosRequestConfig = {}): Promise<AxiosResponse> => {
    const configAPI = await this._getConfig(config);

    return this.instance
      .delete(url, {
        ...config,
        ...configAPI
      })
      .then((response) => response);
  };
}

export const axiosInstance = new SetupAxios();
